import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User, Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import i18n, { SUPPORTED_LANGS, persistLanguage, type SupportedLang } from "@/i18n";
import { toast } from "@/hooks/use-toast";

type AppRole = "owner" | "coach";

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  onboardingCompleted: boolean | null;
  welcomeTourCompleted: boolean | null;
  roles: AppRole[];
  isOwner: boolean;
  isCoach: boolean;
  refreshProfile: () => Promise<void>;
  signUp: (
    email: string,
    password: string,
    displayName: string,
    acceptedTerms?: boolean,
    captchaToken?: string,
  ) => Promise<void>;
  signIn: (email: string, password: string, captchaToken?: string) => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [onboardingCompleted, setOnboardingCompleted] = useState<boolean | null>(null);
  const [welcomeTourCompleted, setWelcomeTourCompleted] = useState<boolean | null>(null);
  const [roles, setRoles] = useState<AppRole[]>([]);

  const fetchProfile = async (userId: string) => {
    const { data, error } = await supabase
      .from("profiles")
      .select("onboarding_completed, welcome_tour_completed, preferred_language")
      .eq("id", userId)
      .maybeSingle();

    if (error) {
      console.error("Failed to fetch profile:", error);
      return;
    }

    setOnboardingCompleted(typeof data?.onboarding_completed === "boolean" ? data.onboarding_completed : false);
    setWelcomeTourCompleted(typeof (data as any)?.welcome_tour_completed === "boolean" ? (data as any).welcome_tour_completed : false);

    // Apply preferred language from profile.
    // Three cases:
    //  1. No preference set → do nothing (i18n keeps detected/default language).
    //  2. Preference is supported → apply it.
    //  3. Preference is set but NOT supported (e.g. legacy / removed locale)
    //     → fall back to default language, notify the user, and clear the
    //       invalid value in the profile so we don't warn again.
    const pref = (data as any)?.preferred_language as string | null | undefined;
    if (pref) {
      const normalized = pref.split("-")[0];
      if ((SUPPORTED_LANGS as readonly string[]).includes(normalized)) {
        const current = (i18n.resolvedLanguage || i18n.language || "").split("-")[0];
        if (current !== normalized) {
          i18n.changeLanguage(normalized as SupportedLang);
          persistLanguage(normalized);
          if (typeof document !== "undefined") {
            document.documentElement.lang = normalized;
          }
        }
      } else {
        // Unsupported language → fallback to i18n default (fallbackLng = "fr")
        const fallback = (i18n.options.fallbackLng as string) || "fr";
        const fallbackLng: SupportedLang = (SUPPORTED_LANGS as readonly string[]).includes(fallback)
          ? (fallback as SupportedLang)
          : ("fr" as SupportedLang);

        i18n.changeLanguage(fallbackLng);
        persistLanguage(fallbackLng);
        if (typeof document !== "undefined") {
          document.documentElement.lang = fallbackLng;
        }

        // Inform the user (translated, with the unsupported code interpolated).
        const fallbackLabel = i18n.t(
          fallbackLng === "fr" ? "common.french" : "common.english",
          fallbackLng.toUpperCase(),
        );
        toast({
          variant: "destructive",
          title: i18n.t(
            "settings.general.languageUnavailableTitle",
            "Langue indisponible",
          ),
          description: i18n.t("settings.general.languageUnavailableDescription", {
            requested: pref,
            fallback: fallbackLabel,
            defaultValue: `La langue « ${pref} » de votre profil n'est plus disponible. Retour automatique à ${fallbackLabel}.`,
          }),
        });

        // Reset the stored preference so we stop warning on every reload.
        // Best-effort: ignore errors silently.
        supabase
          .from("profiles")
          .update({ preferred_language: null } as any)
          .eq("id", userId)
          .then(({ error: updateError }) => {
            if (updateError) {
              console.warn("Could not reset invalid preferred_language:", updateError);
            }
          });
      }
    }
  };

  const fetchRoles = async (userId: string) => {
    const { data, error } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);

    if (error) {
      console.error("Failed to fetch roles:", error);
      return;
    }

    setRoles((data || []).map((r: any) => r.role as AppRole));
  };

  const refreshProfile = async () => {
    if (user) {
      await fetchProfile(user.id);
      await fetchRoles(user.id);
    }
  };

  useEffect(() => {
    let mounted = true;
    let settled = false;

    const finishLoading = () => {
      if (!mounted || settled) return;
      settled = true;
      setLoading(false);
    };

    const authTimeout = window.setTimeout(() => {
      console.warn("Supabase auth session check timed out; continuing without blocking initial render.");
      finishLoading();
    }, 4000);

    const { data: { subscription: authSub } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!mounted) return;
      setSession(session);
      // Keep the same `user` object reference when only the token changed.
      // Supabase emits TOKEN_REFRESHED (and sometimes SIGNED_IN on focus) with
      // a fresh User object every time, which invalidates every downstream
      // `useCallback([user])` (notably AccountContext.refresh) and produces a
      // visible flash on every tab refocus. We only replace `user` when the
      // logical user actually changes (id or email).
      const nextUser = session?.user ?? null;
      setUser((prev) => {
        if (prev && nextUser && prev.id === nextUser.id && prev.email === nextUser.email) {
          return prev;
        }
        return nextUser;
      });
      if (session?.user) {
        // Only refetch profile/roles on a *real* identity change, not on a
        // token refresh — otherwise we'd re-run RLS-guarded queries every hour.
        if (_event !== "TOKEN_REFRESHED") {
          setTimeout(() => {
            fetchProfile(session.user.id);
            fetchRoles(session.user.id);
          }, 0);
        }
      } else {
        setOnboardingCompleted(null);
        setWelcomeTourCompleted(null);
        setRoles([]);
      }
      window.clearTimeout(authTimeout);
      finishLoading();
    });


    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      setSession(session);
      setUser(session?.user ?? null);
      if (session?.user) {
        fetchProfile(session.user.id);
        fetchRoles(session.user.id);
      }
      window.clearTimeout(authTimeout);
      finishLoading();
    }).catch((error) => {
      console.error("Failed to resolve initial auth session:", error);
      window.clearTimeout(authTimeout);
      finishLoading();
    });

    return () => {
      mounted = false;
      window.clearTimeout(authTimeout);
      authSub.unsubscribe();
    };
  }, []);

  const signUp = async (
    email: string,
    password: string,
    displayName: string,
    acceptedTerms?: boolean,
    captchaToken?: string,
  ) => {
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName },
        emailRedirectTo: `${window.location.origin}/auth?confirmed=true`,
        ...(captchaToken ? { captchaToken } : {}),
      },
    });
    if (error) throw error;

    // Persist the legal acceptance timestamp on the freshly-created profile
    // (the profile row is created automatically by the handle_new_user trigger).
    // Best-effort : on n'échoue pas l'inscription si l'update ne passe pas
    // (RLS/timing), l'utilisateur peut toujours se connecter.
    if (acceptedTerms && data?.user?.id) {
      try {
        await supabase
          .from("profiles")
          .update({
            terms_accepted_at: new Date().toISOString(),
            terms_accepted_version: "2026-04-23",
          } as any)
          .eq("id", data.user.id);
      } catch (e) {
        console.warn("Could not persist terms acceptance:", e);
      }
    }
    // NOTE : l'email de bienvenue n'est volontairement PAS envoyé ici.
    // Il est déclenché APRÈS que l'utilisateur a cliqué sur le lien de
    // confirmation reçu par email (voir src/pages/Auth.tsx, effet sur
    // ?confirmed=true). Cela évite que les deux emails (confirmation +
    // bienvenue) n'arrivent en même temps dans la boîte du nouvel inscrit.
  };

  const signIn = async (email: string, password: string, captchaToken?: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
      ...(captchaToken ? { options: { captchaToken } } : {}),
    });
    if (error) throw error;
  };

  const signOut = async () => {
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  };

  const isOwner = roles.includes("owner");
  const isCoach = roles.includes("coach");

  return (
    <AuthContext.Provider value={{ user, session, loading, onboardingCompleted, welcomeTourCompleted, roles, isOwner, isCoach, refreshProfile, signUp, signIn, signOut }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
}
